import { useKrakenQuery } from '@/components/GraphQLClient';
import { useAuth } from '@/components/Providers/Auth';
import { useAccountNumberContext } from '@/context/AccountNumberContext/withFetch/AccountNumberContext';
import { useCurrentActivePaymentSchedule } from '@/hooks/billsAndPayments/useCurrentActivePaymentSchedule';
import { useLedgers } from '@/hooks/billsAndPayments/useLedgers';
import { UsePaymentForecastArgs } from '@/hooks/billsAndPayments/usePaymentForecast/index.types';
import PaymentForecast from '@/hooks/billsAndPayments/usePaymentForecast/PaymentForecast.graphql';
import { paymentForecastSchema } from '@/hooks/billsAndPayments/validations';
import { getCorrectLedgerNumber } from '@/utils/ledgers';

export const usePaymentForecast = ({ first }: UsePaymentForecastArgs) => {
  const { isAuthenticated } = useAuth();
  const { accountNumber } = useAccountNumberContext();

  const { data: ledgerData } = useLedgers();

  const { data: currentActivePaymentSchedule, isSuccess } =
    useCurrentActivePaymentSchedule();

  // we cannot retrieve payment forecast for payment schedule with
  // variable direct debit schedule, as defined in the backend
  // ref: https://github.com/octoenergy/kraken-core/blob/dc8e0b0989b2177835e8670b0192a85d1653e2d9/src/octoenergy/application/usecases/payments/schedules.py#L739
  const isVariablePaymentSchedule =
    !!currentActivePaymentSchedule?.node?.isVariablePaymentAmount;

  const ledgerNumber = getCorrectLedgerNumber(ledgerData);

  return useKrakenQuery({
    queryKey: ['paymentForecast'],
    query: PaymentForecast,
    select: (data: any) => data.account.paginatedPaymentForecast.edges,
    variables: { accountNumber, ledgerNumber, first },
    validateFn(response) {
      return paymentForecastSchema.validate(response);
    },
    gcTime: 0,
    enabled: Boolean(
      isAuthenticated &&
        accountNumber &&
        ledgerNumber &&
        first &&
        isSuccess &&
        !isVariablePaymentSchedule
    ),
    refetchOnMount: 'always',
  });
};
